<script setup>
import { computed, ref, toRefs, useTemplateRef, watch } from 'vue';
import { useRoute } from 'vue-router';
import {
  useConfirmationDialog,
  useToast,
  useDrawer,
  HorizontalBarChart,
  MetricList,
  Formatters,
} from '@dashhudson/dashing-ui';
import constants, {
  REPORT_MODES,
  IMAGE_DOWNLOAD_FILE_FORMATS,
  REPORT_ERROR_MESSAGES,
  DASHBOARD_DRAWERS,
} from '@/app/dashboards/constants';
import { isWidgetReport, REPORTS } from '@/app/dashboards/utils/reports.enum';
import { useServerErrors } from '@/composables/useServerErrors';
import Icon from '@/components/foundation/Icon.vue';
import { logger } from '@/utils/logger';
import {
  dashboardReportLoading,
  downloadImage,
} from '@/app/dashboards/utils/dashboard-general.util';
import { useDashboardReportsStore } from '@/stores/dashboards-reports';
import { useDashboardsStore } from '@/stores/dashboards';
import { useSearchStore } from '@/stores/search';
import { useElementVisibility } from '@vueuse/core';
import { useCustomMetrics } from '@/app/settings/composables/customMetrics';
import { useReportDetails } from '@/app/dashboards/composables/useReportDetails';
import { useDashboards } from '@/app/dashboards/composables/useDashboards';
import { useReportErrors } from '@/app/dashboards/composables/useReportErrors';
import { reportComponent } from '@/app/dashboards/utils/report.utils';
import { useReportMenu } from '@/app/dashboards/composables/useReportMenu';
import { storeToRefs } from 'pinia';
import { useMetricsStore } from '@/stores/metrics';
import { useReportType } from '@/app/dashboards/composables/useReportType';
import { DISPLAY_STRATEGY, REPORT_MENU_ITEMS } from '@/app/dashboards/reportConstants';
import { useReport } from '@/app/dashboards/composables/useReport';
import { FORMAT_TYPES } from '@/models/dashboards/metrics.constants';
import { useFilterOptions } from '@/app/dashboards/composables/useFilterOptions';
import { useMetricFormatting } from '@/app/dashboards/composables/useMetricFormattingSetup';
import { useIdentityStore } from '@/stores/identity';

const props = defineProps({
  reportOverride: { type: Object, default: null },
  reportId: { type: Number, default: null },
  type: { type: String, required: true },
  visionAiReport: { type: Boolean, default: false },
});

const emit = defineEmits(['removeItem']);

const { reportOverride, reportId, type, visionAiReport } = toRefs(props);
const serverErrorMessage = ref(null);
const needDataRefresh = ref(false);
const target = useTemplateRef('target');
const visibleDatasetIndexes = ref(null);

const identityStore = useIdentityStore();
const { customMetricNotFound } = useCustomMetrics();
const dashboardReportsStore = useDashboardReportsStore();
const dashboardsStore = useDashboardsStore();
const { canAccessGDIDashboards } = useDashboards();
const searchStore = useSearchStore();
const { addToast } = useToast({ life: 5000 });
const route = useRoute();
const { onServerError } = useServerErrors();
const { launchDrawer } = useDrawer();
const { metricDetailsReady } = storeToRefs(useMetricsStore());
const { isGraphReport, isDoughnutChart, isHorizontalBarChart, isBarChart } = useReportType({
  reportType: type,
});

const comparisonDate = computed(() => {
  if (!reportId.value) return null;
  return dashboardReportsStore.contextDateRange;
});

const report = computed(() => {
  if (reportOverride.value) return reportOverride.value;
  return dashboardsStore.reports[reportId.value];
});

const reportIndex = computed(() => (reportOverride.value ? 'highlightedReport' : reportId.value));

const error = computed(() => dashboardsStore.reportError[reportIndex.value]);

const loading = computed(() => {
  if (!reportId.value) return false;
  if (!metricDetailsReady.value) return true;
  if (serverErrorMessage.value) return false;
  if (error.value) return false;
  return dashboardReportLoading(reportId.value, type.value, dashboardsStore, searchStore);
});

const isCustomMetricDeleted = computed(() => {
  const metrics = report.value?.meta?.metrics || [];
  const firstMetric = metrics[0];
  const isNotCustomMetric = !firstMetric?.startsWith('CM_');

  if (!firstMetric || isNotCustomMetric) return false;
  return customMetricNotFound(firstMetric);
});

const customClasses = computed(() => ({
  simple: isWidgetReport(type.value) && !dashboardsStore.dashboardCustomizeMode,
}));

const isReportRoute = computed(() => route?.meta?.report);

const reportingDate = computed(() => {
  if (!reportId.value) return null;

  return dashboardReportsStore.reportDateRange;
});

const isReportExpanded = computed(
  () => (reportOverride.value && reportOverride.value.isExpanded) || null,
);

const { errorMessageForDisplay: reportErrorMessage, showErrorState } = useReportErrors({
  report,
  loading,
  serverErrorMessage,
});

const errorState = computed(
  () =>
    !loading.value &&
    (serverErrorMessage.value || isCustomMetricDeleted.value || showErrorState.value),
);

const errorMessage = computed(() => {
  if (serverErrorMessage.value) return REPORT_ERROR_MESSAGES.SERVER_ERROR;
  if (isCustomMetricDeleted.value) return REPORT_ERROR_MESSAGES.CUSTOM_METRIC_NOT_FOUND;
  return reportErrorMessage.value;
});

function editReport() {
  launchDrawer({
    name: DASHBOARD_DRAWERS.REPORT_FORM_DRAWER,
    props: { mode: REPORT_MODES.EDIT.value, report: report.value },
  });
}

function duplicateReport() {
  launchDrawer({
    name: DASHBOARD_DRAWERS.REPORT_FORM_DRAWER,
    props: { mode: REPORT_MODES.DUPLICATE.value, report: report.value },
  });
}
const {
  customizeDatesMenuItem,
  deleteCommandProps,
  disableCSV,
  disableJPEG,
  hasStaticDates,
  meetsYTCompliance,
  showJPEGInReportMenu,
} = useReportMenu({
  reportId,
  loading,
});

const { showConfirmationDialog: showDeleteConfirmationDialog } = useConfirmationDialog({
  group: 'global',
  header: deleteCommandProps.value.confirmDeleteTitle,
  message: deleteCommandProps.value.confirmDeleteMessage,
  acceptLabel: deleteCommandProps.value.confirmDeleteButtonLabel,
  acceptSeverity: deleteCommandProps.value.acceptSeverity,
  onAccept: async () => {
    try {
      await dashboardsStore.deleteReport(report.value || { id: reportId.value });
      addToast({
        severity: 'success',
        message: deleteCommandProps.value.deleteSuccessMessage,
      });
    } catch (err) {
      logger.error('Failed to delete report', {}, err);
      addToast({
        severity: 'error',
        message: deleteCommandProps.value.deleteErrorMessage,
      });
    }
  },
});

const { showConfirmationDialog: showRemoveCustomDateConfirmationDialog } = useConfirmationDialog({
  group: 'global',
  header: REPORT_MENU_ITEMS.REMOVE_CUSTOM_DATE.confirmDeleteTitle,
  message: REPORT_MENU_ITEMS.REMOVE_CUSTOM_DATE.confirmDeleteMessage,
  acceptSeverity: REPORT_MENU_ITEMS.REMOVE_CUSTOM_DATE.acceptSeverity,
  acceptLabel: REPORT_MENU_ITEMS.REMOVE_CUSTOM_DATE.confirmDeleteButtonLabel,
  onAccept: async () => {
    try {
      await dashboardsStore.deleteReportStaticDates({ reportId: report.value?.id });
      addToast({
        severity: 'success',
        message: REPORT_MENU_ITEMS.REMOVE_CUSTOM_DATE.deleteSuccessMessage,
      });
    } catch (err) {
      logger.error('Failed to delete report', {}, err);
      addToast({
        severity: 'error',
        message: REPORT_MENU_ITEMS.REMOVE_CUSTOM_DATE.deleteErrorMessage,
      });
    }
  },
});

async function downloadCSV() {
  const { messageTagFilterOptions } = useFilterOptions({
    selectedBrandIds: report.value?.meta?.brand_ids,
  });

  try {
    await dashboardsStore.downloadReportCSV({
      report: report.value,
      messageTagFilterOptions: messageTagFilterOptions.value,
    });
    addToast({
      severity: 'success',
      message: 'CSV downloaded successfully',
    });
  } catch (err) {
    logger.error('Dashboard Download CSV Failed', {}, err);
    addToast({
      severity: 'error',
      message: 'Unable to download CSV. Please try again',
    });
  }
}

async function downloadJPEG() {
  const element = document.querySelector(`[data-cy="ReportPanel-${reportId.value}"]`);
  try {
    await downloadImage(element, IMAGE_DOWNLOAD_FILE_FORMATS.JPEG.extension);
    addToast({
      severity: 'success',
      message: 'JPEG downloaded successfully',
    });
  } catch (err) {
    logger.error('Dashboard Download JPEG Failed', {}, err);
    addToast({
      severity: 'error',
      message: 'Unable to download JPEG. Please try again',
    });
  }
}

const graphScale = computed(() => dashboardReportsStore.graphScale);
const reportDetails = useReportDetails({
  loading,
  graphScale,
  report,
  visibleDatasetIndexes,
  serverErrorMessage,
});

const menuItems = computed(() => {
  if (loading.value || isReportRoute.value || visionAiReport.value || identityStore.isShared) {
    return null;
  }
  const actions = [];
  actions.push({
    ...REPORT_MENU_ITEMS.EDIT,
    command: editReport,
    roles: [constants.ROLES.OWNER.value, constants.ROLES.EDITOR.value],
  });

  actions.push(customizeDatesMenuItem.value);

  actions.push({
    ...REPORT_MENU_ITEMS.DUPLICATE,
    command: duplicateReport,
    roles: [constants.ROLES.OWNER.value, constants.ROLES.EDITOR.value],
  });

  if (meetsYTCompliance.value) {
    actions.push({
      ...REPORT_MENU_ITEMS.DOWNLOAD_CSV,
      command: downloadCSV,
      disabled: disableCSV.value,
    });
  }

  if (showJPEGInReportMenu.value) {
    actions.push({
      ...REPORT_MENU_ITEMS.DOWNLOAD_JPEG,
      command: downloadJPEG,
      disabled: disableJPEG.value,
    });
  }

  actions.push({
    ...REPORT_MENU_ITEMS.REMOVE_CUSTOM_DATE,
    roles: [constants.ROLES.OWNER.value, constants.ROLES.EDITOR.value],
    command: showRemoveCustomDateConfirmationDialog,
    disabled: !hasStaticDates.value,
  });

  actions.push({
    ...REPORT_MENU_ITEMS.DELETE,
    roles: [constants.ROLES.OWNER.value, constants.ROLES.EDITOR.value],
    command: showDeleteConfirmationDialog,
  });

  return actions.filter(
    (action) =>
      !Array.isArray(action.roles) || action.roles.includes(dashboardsStore.currentDashboardRole),
  );
});

const reportComponentProps = computed(() => {
  const {
    singleMetricReportProps,
    graphReportProps,
    tableMetricReportProps,
    noteReportProps,
    headerReportProps,
  } = reportDetails;

  switch (REPORTS[type.value]?.components?.report?.name) {
    case 'GroupedMetricReportPanel':
      return singleMetricReportProps.value;
    case 'GraphReportPanel':
      return graphReportProps.value;
    case 'MetricReportTablePanel':
      return tableMetricReportProps.value;
    case 'NoteReportPanel':
      return noteReportProps.value;
    case 'HeaderReportPanel':
      return headerReportProps.value;
    default:
      return null;
  }
});

function onDeleteReport() {
  emit('removeItem');
}

function onVisibleDatasets(indexes) {
  if (!isDoughnutChart.value) return;
  visibleDatasetIndexes.value = indexes;
}

const targetIsVisible = useElementVisibility(target);

const showReportContent = computed(
  () => (targetIsVisible.value || isReportRoute.value) && !serverErrorMessage.value,
);

const showGdiReportContent = computed(() => {
  if (!canAccessGDIDashboards.value) return false;
  return targetIsVisible.value || isReportRoute.value;
});

const gdiLoadingState = computed(() => {
  if (serverErrorMessage.value) return false;
  return loading.value;
});

const {
  currency,
  customMetricDetails,
  displayStrategy,
  firstMetric,
  isCustomMetricReportTile,
  reportMetricDetails,
} = useReport({
  report,
});

const { getFormatConfig } = useMetricFormatting({
  dataType: isCustomMetricReportTile.value
    ? customMetricDetails.value.metricFormat
    : reportMetricDetails.value.dataType,
  isCustomMetricReportTile,
  customMetricDetails,
  currency,
  displayStrategy,
});

function formatMetricValue(value) {
  const config = getFormatConfig({
    value,
    disableAbbreviation: true,
  });

  return Formatters.formatNumber(value, config.format, config);
}

const useCustomMeterValues = computed(
  () => displayStrategy.value === DISPLAY_STRATEGY.PERCENT_OF_GRAND_TOTAL,
);

watch(
  reportId,
  (id) => {
    if (!id) {
      return;
    }

    needDataRefresh.value = true;
  },
  { immediate: true },
);

watch(
  () => dashboardReportsStore.graphScale,
  () => {
    if (!isGraphReport.value || isBarChart.value) return;
    needDataRefresh.value = true;
  },
);

watch(
  error,
  () => {
    if (!error.value) {
      serverErrorMessage.value = null;
      return;
    }
    serverErrorMessage.value = onServerError(error.value, { notification: false });
    logger.error(serverErrorMessage.value, {}, error.value);
  },
  { deep: true },
);

watch(
  () => dashboardsStore.updatingDashboard,
  (updating) => {
    // Refresh only the reports on the screen after the dashboard is updated
    if (!updating) {
      needDataRefresh.value = true;
    }
  },
);

watch(
  () => [targetIsVisible.value, needDataRefresh.value],
  async () => {
    if ((targetIsVisible.value && needDataRefresh.value) || isReportRoute.value) {
      needDataRefresh.value = false;
      // Reset visible dataset indexes when fetching fresh data
      visibleDatasetIndexes.value = null;
      await dashboardsStore.getReportData({ reportId: reportId.value, refresh: true });
    }
  },
  { immediate: true },
);
</script>

<template>
  <div
    ref="target"
    :class="[
      'dash-w-full dash-h-full',
      { 'dashboard-report-item-content': !canAccessGDIDashboards },
      customClasses,
    ]"
    :data-cy="`ReportPanel-${reportId}`"
  >
    <template v-if="errorState && !canAccessGDIDashboards">
      <div class="error-panel">
        <Icon v-tooltip="serverErrorMessage" xlarge name="alertCircle" class="error-icon" />
        <div class="error-message">{{ errorMessage }}</div>
      </div>
    </template>
    <component
      :is="reportComponent(type, canAccessGDIDashboards)"
      v-else-if="showGdiReportContent"
      v-bind="reportComponentProps"
      :customization-mode="dashboardsStore.dashboardCustomizeMode"
      :loading="gdiLoadingState"
      :preview="false"
      :report="report"
      :type="type"
      :menu-items="menuItems"
      @delete-report="onDeleteReport"
      @visible-datasets="onVisibleDatasets"
    >
      <template v-if="isHorizontalBarChart" #chart>
        <HorizontalBarChart
          :meters="reportComponentProps.chartData?.meters"
          :markers="reportComponentProps.chartData?.markers"
          :min="reportComponentProps.chartOptions?.min"
          :max="reportComponentProps.chartOptions?.max"
          show-values
          :disable-sorting="reportComponentProps.chartData?.disableSorting"
          :show-legend="reportComponentProps.showLegend ?? false"
          @visible-datasets="onVisibleDatasets"
        >
          <template #tooltip="{ context, meter, titleCallback }">
            {{ titleCallback(meter.label) }}
            <MetricList
              :metric-items="[{ label: context.label, value: formatMetricValue(context.value) }]"
            />
          </template>
          <template v-if="useCustomMeterValues" #total="{ meter }">
            {{ meter.customValue }}</template
          >
        </HorizontalBarChart>
      </template>
      <template v-if="reportComponentProps?.customTooltipCallback" #tooltip="tooltipContext">
        <MetricList :metric-items="reportComponentProps.customTooltipCallback(tooltipContext)" />
      </template>
    </component>
    <component
      :is="reportComponent(type, canAccessGDIDashboards)"
      v-else-if="showReportContent"
      :report="report"
      :type="type"
      :loading="loading"
      :reporting-date="reportingDate"
      :comparison-date="comparisonDate"
      :is-report-expanded="isReportExpanded"
      disabled-static-date-range
    />
  </div>
</template>

<style lang="postcss" scoped>
.dashboard-report-item-content {
  height: 100%;
  border-radius: 6px;
  background-color: var(--background-0);
  box-shadow: var(--shadow-1);
  transition: none;
  -webkit-print-color-adjust: exact;

  &.simple {
    background: none;
    box-shadow: none;
  }

  .error-panel {
    padding: var(--space-16);

    .error-message {
      margin-top: var(--x24);
      color: var(--text-secondary);
    }
  }

  .single-metric-report {
    height: 210px;
  }
}

.line-height-style {
  line-height: 0.5rem;
}

@media print {
  .dashboard-report-item-content {
    border: 1px solid var(--border);
    box-shadow: none;
  }
}
</style>
